import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VirtualTable,
  PillCRA as Pill,
  Empty,
  useTheme,
} from '@prio365/prio365-react-library';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import { makePrioStyles } from '../../theme/utils';
import { TableSkeleton } from './TaskHubSkeletons';
import moment from 'moment';
import { ExtendedTaskDto } from './api/taskHubApi';

const useStyles = makePrioStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  tableCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing.regular,
    backgroundColor: theme.colors.application.background.default,
    borderRadius: theme.borderRadius.regular,
    border: `1px solid ${theme.colors.application.border}`,
    minWidth: 0,
    overflow: 'hidden',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    paddingBottom: theme.spacing.small,
    borderBottom: `1px solid ${theme.colors.application.border}`,
    marginBottom: theme.spacing.regular,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tableContainer: {
    flex: 1,
    minHeight: '300px',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  assigneeList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.small,
    minWidth: 0,
  },
  labelList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.small,
    minWidth: 0,
    maxWidth: '100%',
  },
  labelPill: {
    fontSize: '11px',
    padding: '2px 6px',
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dateText: {
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  overdueDate: {
    color: theme.colors.base.red.default,
    fontWeight: 500,
  },
  bucketText: {
    fontSize: '11px',
    color: theme.colors.application.typography.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 2,
  },
  projectText: {
    fontSize: '12px',
    color: theme.colors.application.typography.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 500,
  },
  tableContent: {
    width: '100%',
    height: '100%',
  },
  titleCell: {
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
    paddingLeft: theme.spacing.small,
  },
  titleText: {
    fontWeight: 500,
    marginBottom: 4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '14px',
    lineHeight: '1.2',
  },
  cellContainer: {
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },
  pillContainer: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  noLabelsText: {
    color: theme.colors.application.typography.muted,
    fontSize: '12px',
    fontStyle: 'italic',
  },
}));

interface TaskHubTableProps {
  tasks: ExtendedTaskDto[];
  onTaskDetail: (task: ExtendedTaskDto) => void;
  loading?: boolean;
  title?: string;
}

const TaskHubTable: React.FC<TaskHubTableProps> = ({
  tasks,
  onTaskDetail,
  loading = false,
  title,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  // Helper functions wrapped in useCallback to prevent unnecessary re-renders
  const getStatusColor = useCallback(
    (status: string): 'primary' | 'default' | 'neutral' => {
      switch (status.toLowerCase()) {
        case 'completed':
          return 'primary';
        case 'inprogress':
          return 'default';
        default:
          return 'neutral';
      }
    },
    []
  );

  const getPriorityColor = useCallback(
    (priority: string): 'primary' | 'default' | 'neutral' => {
      switch (priority.toLowerCase()) {
        case 'high':
          return 'primary';
        case 'medium':
          return 'default';
        default:
          return 'neutral';
      }
    },
    []
  );

  const getPriorityState = useCallback(
    (priority: string): 'green' | 'red' | undefined => {
      switch (priority.toLowerCase()) {
        case 'high':
          return 'red';
        case 'low':
          return 'green';
        default:
          return undefined;
      }
    },
    []
  );

  const isOverdue = useCallback((dueDate: string | undefined): boolean => {
    if (!dueDate) return false;
    return moment(dueDate).isBefore(moment(), 'day');
  }, []);

  const formatDate = useCallback((dateString: string | undefined): string => {
    if (!dateString) return '-';
    return moment(dateString).format('DD.MM.YY');
  }, []);

  const getStatusLabel = useCallback(
    (status: string): string => {
      switch (status.toLowerCase()) {
        case 'notstarted':
          return t('taskHub:table.status.notStarted');
        case 'inprogress':
          return t('taskHub:table.status.inProgress');
        case 'completed':
          return t('taskHub:table.status.completed');
        default:
          return status;
      }
    },
    [t]
  );

  const getPriorityLabel = useCallback(
    (priority: string): string => {
      switch (priority.toLowerCase()) {
        case 'high':
          return t('taskHub:table.priority.high');
        case 'medium':
          return t('taskHub:table.priority.medium');
        case 'low':
          return t('taskHub:table.priority.low');
        case 'urgent':
          return t('taskHub:table.priority.urgent');
        case 'important':
          return t('taskHub:table.priority.important');
        default:
          return priority.charAt(0).toUpperCase() + priority.slice(1);
      }
    },
    [t]
  );

  // UPDATED: Table columns with Labels column
  const columns: Column<ExtendedTaskDto>[] = useMemo(
    () => [
      {
        id: 'title',
        title: t('taskHub:table.columns.task'),
        accessor: 'title',
        width: 30, // Reduced from 35 to make room for labels
        Cell: ({ value, originalData }) => (
          <div className={classes.titleCell}>
            <div
              className={classes.titleText}
              title={`${value} - ${originalData.bucket}`}
            >
              {value}
            </div>
          </div>
        ),
        sortingFn: (a, b) => a.title.localeCompare(b.title),
      },
      {
        id: 'project',
        title: t('taskHub:table.columns.project'),
        accessor: 'projectDisplayName',
        width: 12, // Reduced from 15
        Cell: ({ value }) => (
          <div
            className={classes.projectText}
            title={value || 'Unknown Project'}
          >
            {value || 'Unknown Project'}
          </div>
        ),
        sortingFn: (a, b) => {
          const aProject = a.projectDisplayName || 'Unknown Project';
          const bProject = b.projectDisplayName || 'Unknown Project';
          return aProject.localeCompare(bProject);
        },
      },
      {
        id: 'status',
        title: t('taskHub:table.columns.status'),
        accessor: 'status',
        width: 8, // Reduced from 10
        Cell: ({ value }) => (
          <div className={classes.pillContainer}>
            <Pill type={getStatusColor(value)} size="small">
              {getStatusLabel(value)}
            </Pill>
          </div>
        ),
        sortingFn: (a, b) => a.status.localeCompare(b.status),
      },
      {
        id: 'priority',
        title: t('taskHub:table.columns.priority'),
        accessor: 'priority',
        width: 8, // Reduced from 10
        Cell: ({ value }) => (
          <div className={classes.pillContainer}>
            <Pill
              type={getPriorityColor(value)}
              state={getPriorityState(value)}
              size="small"
            >
              {getPriorityLabel(value)}
            </Pill>
          </div>
        ),
        sortingFn: (a, b) => a.priority.localeCompare(b.priority),
      },
      {
        id: 'labels',
        title: t('taskHub:dashboard.filters.labels'), // Using existing translation
        accessor: 'labels',
        width: 15, // NEW: Labels column
        Cell: ({ value }) => {
          if (!value || value.length === 0) {
            return <span className={classes.noLabelsText}>-</span>;
          }

          // Show first 2 labels directly, then "+X" as separate pill
          const visibleLabels = value.slice(0, 2);
          const remainingCount = value.length - 2;

          return (
            <div
              className={classes.labelList}
              title={`Labels: ${value.join(', ')}`}
            >
              {visibleLabels.map((label, index) => (
                <Pill key={`${label}-${index}`} type="neutral" size="small">
                  {label}
                </Pill>
              ))}
              {remainingCount > 0 && (
                <Pill type="neutral" size="small">
                  +{remainingCount}
                </Pill>
              )}
            </div>
          );
        },
        sortingFn: (a, b) => {
          const aLabels = a.labels.join(', ').toLowerCase();
          const bLabels = b.labels.join(', ').toLowerCase();
          return aLabels.localeCompare(bLabels);
        },
      },
      {
        id: 'assignees',
        title: t('taskHub:table.columns.assignee'),
        accessor: 'assignees',
        width: 12, // Reduced from 15
        Cell: ({ value }) => {
          if (value.length === 0) {
            return (
              <span
                style={{
                  color: theme.colors.application.typography.muted,
                  fontSize: '12px',
                }}
              >
                {t('taskHub:table.unassigned')}
              </span>
            );
          }

          const firstName = value[0];
          const hasMore = value.length > 1;

          return (
            <div className={classes.assigneeList}>
              <Pill type="neutral" size="small">
                {firstName}
                {hasMore && ` +${value.length - 1}`}
              </Pill>
            </div>
          );
        },
      },
      {
        id: 'dueDate',
        title: t('taskHub:table.columns.due'),
        accessor: 'dueDate',
        width: 15,
        Cell: ({ value }) => (
          <div
            className={`${classes.dateText} ${value && isOverdue(value) ? classes.overdueDate : ''}`}
            title={
              value
                ? moment(value).format('DD.MM.YYYY')
                : t('taskHub:table.noDueDate')
            }
          >
            {formatDate(value)}
          </div>
        ),
        sortingFn: (a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return moment(a.dueDate).diff(moment(b.dueDate));
        },
      },
    ],
    [
      theme,
      classes,
      t,
      getPriorityLabel,
      getStatusLabel,
      getStatusColor,
      getPriorityColor,
      getPriorityState,
      isOverdue,
      formatDate,
    ]
  );

  const displayTitle = title || t('taskHub:table.title');

  // Show skeleton if loading
  if (loading) {
    return <TableSkeleton />;
  }

  return (
    <div className={classes.root}>
      <div className={classes.tableCard}>
        <h3
          className={classes.cardTitle}
          title={`${displayTitle} (${tasks.length})`}
        >
          {displayTitle} ({tasks.length})
        </h3>
        <div className={classes.tableContainer}>
          {tasks.length === 0 ? (
            <Empty customMessage={t('taskHub:table.noTasksFound')} />
          ) : (
            <VirtualTable<ExtendedTaskDto>
              id="task-hub-table"
              className={classes.tableContent}
              enableHeader={true}
              data={tasks}
              columns={columns}
              resizable="relative"
              onCheckEquality={(a, b) => a.id === b.id}
              onRow={(task) => ({
                onClick: () => onTaskDetail(task),
                style: {
                  cursor: 'pointer',
                },
              })}
              rowHeight={50}
              useLegacyActionBar
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskHubTable;
